/**
 * mini-cart-recovery.js
 *
 * Empty-cart recovery ribbon for the mini-cart drawer.
 *
 * How it works:
 * - Listens on document.documentElement for the theme's `cart:updated` event.
 * - While cart.item_count > 0, writes a lightweight snapshot of line items to
 *   sessionStorage under `pl:cart:recovery` (variant_id + quantity + properties).
 * - When the empty-cart drawer renders, the <cart-recovery> element checks
 *   sessionStorage. If a snapshot exists and is within the TTL (30 min), it
 *   reveals a ribbon with a Restore action.
 * - Restore POSTs to /cart/add.js with the snapshot items, then fires the same
 *   cart:updated / cart:refresh events the rest of the theme listens to, so the
 *   cart drawer re-renders with the recovered items in place.
 *
 * The snapshot key is namespaced with `pl:` to avoid collision with any
 * upstream Focal storage keys. TTL is intentionally short (30 min) so a stale
 * snapshot never resurfaces days later.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'pl:cart:recovery';
  var TTL_MS = 30 * 60 * 1000;

  function readSnapshot() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || !Array.isArray(data.items) || data.items.length === 0 || !data.ts) {
        return null;
      }
      if (Date.now() - data.ts > TTL_MS) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  function writeSnapshot(items) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        items: items,
        ts: Date.now()
      }));
    } catch (e) {
      // Private-mode Safari and quota errors: silently drop the snapshot.
    }
  }

  function clearSnapshot() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  function toSnapshotItem(item) {
    return {
      id: item.variant_id || item.id,
      quantity: item.quantity,
      properties: item.properties && Object.keys(item.properties).length ? item.properties : null
    };
  }

  function totalUnits(items) {
    return items.reduce(function (n, i) { return n + (i.quantity || 0); }, 0);
  }

  // Persist any non-empty cart state as the recovery snapshot. This runs at
  // document scope so it survives cart-drawer re-renders, and captures the
  // last-known-good cart before item_count transitions to 0.
  document.documentElement.addEventListener('cart:updated', function (event) {
    var cart = event && event.detail && event.detail.cart;
    if (!cart || !Array.isArray(cart.items)) return;
    if (cart.item_count > 0) {
      writeSnapshot(cart.items.map(toSnapshotItem));
    }
  });

  var CartRecovery = class extends HTMLElement {
    connectedCallback() {
      this._render();
      this._boundOnCartUpdate = this._onCartUpdate.bind(this);
      document.documentElement.addEventListener('cart:updated', this._boundOnCartUpdate);

      var restore = this.querySelector('[data-recovery-restore]');
      if (restore) restore.addEventListener('click', this._onRestore.bind(this));

      var dismiss = this.querySelector('[data-recovery-dismiss]');
      if (dismiss) dismiss.addEventListener('click', this._onDismiss.bind(this));
    }

    disconnectedCallback() {
      if (this._boundOnCartUpdate) {
        document.documentElement.removeEventListener('cart:updated', this._boundOnCartUpdate);
      }
    }

    _render() {
      var snap = readSnapshot();
      if (!snap) {
        this.hidden = true;
        return;
      }
      var count = totalUnits(snap.items);
      var countEl = this.querySelector('[data-recovery-count]');
      if (countEl) {
        countEl.textContent = count + ' ' + (count === 1 ? 'item' : 'items');
      }
      this.hidden = false;
    }

    _onCartUpdate(event) {
      var cart = event && event.detail && event.detail.cart;
      if (!cart) return;
      if (cart.item_count > 0) {
        this.hidden = true;
      } else {
        this._render();
      }
    }

    _onDismiss(event) {
      event.preventDefault();
      clearSnapshot();
      this.hidden = true;
    }

    async _onRestore(event) {
      event.preventDefault();
      var snap = readSnapshot();
      if (!snap) return;

      var button = this.querySelector('[data-recovery-restore]');
      var originalLabel = button ? button.textContent : '';
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = 'Restoring...';
      }

      try {
        var items = snap.items.map(function (i) {
          var payload = { id: i.id, quantity: i.quantity };
          if (i.properties) payload.properties = i.properties;
          return payload;
        });

        var addResponse = await fetch(window.themeVariables.routes.cartAddUrl + '.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items: items, sections: ['mini-cart'] })
        });

        if (!addResponse.ok) throw new Error('cart add failed');

        var cartResponse = await fetch(window.themeVariables.routes.cartUrl + '.js', {
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' }
        });
        var cart = await cartResponse.json();

        clearSnapshot();

        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cart }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart: cart, replacementDelay: 400 }
        }));
      } catch (err) {
        console.warn('[cart-recovery] Restore failed:', err);
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.textContent = originalLabel;
        }
      }
    }
  };

  if (!window.customElements.get('cart-recovery')) {
    window.customElements.define('cart-recovery', CartRecovery);
  }
})();
